import { AppShell } from '@mantine/core';
import { Suspense, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { PageSkeleton } from '../../components/PageSkeleton';
import { MOBILE_BREAKPOINT, useIsMobile } from '../useIsMobile';
import { BottomBar } from './BottomBar';
import { Header } from './Header';
import { MobileDrawer } from './MobileDrawer';
import { NavFooter, NavItems } from './NavItems';
import { ViewAsBanner } from './ViewAsBanner';
import { AnnouncementsBanner } from '../../features/operations/AnnouncementsBanner';
import { RealtimeProvider } from '../realtime';
import classes from './shell.module.css';

const TRANSITION_MS = 380;

/** "/hr/holiday/approvals/riley" → "/hr/holiday": list↔detail moves within a page don't re-skeleton. */
function pageKeyOf(pathname: string): string {
  return pathname.split('/').slice(0, 3).join('/');
}

/**
 * Page changes show a skeleton for a beat (never a spinner). The outgoing/incoming page
 * stays mounted underneath so its data starts loading straight away.
 */
function usePageTransition(): boolean {
  const { pathname } = useLocation();
  const pageKey = pageKeyOf(pathname);
  const [settledKey, setSettledKey] = useState(pageKey);
  useEffect(() => {
    if (settledKey === pageKey) return;
    const t = window.setTimeout(() => setSettledKey(pageKey), TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [pageKey, settledKey]);
  return settledKey !== pageKey;
}

export function Shell() {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const transitioning = usePageTransition();
  const mainRef = useRef<HTMLDivElement>(null);

  // New page: start at the top. Overlays close themselves via their onNavigate callbacks.
  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [pathname]);

  const headerHeight = isMobile && searchOpen ? 56 + 63 : 56;

  return (
    <RealtimeProvider>
      <AppShell
        header={{ height: headerHeight }}
        navbar={{ width: 232, breakpoint: MOBILE_BREAKPOINT, collapsed: { mobile: true } }}
        footer={{ height: 64, collapsed: !isMobile }}
        padding={0}
        withBorder={false}
      >
        <AppShell.Header>
          <Header searchOpen={searchOpen} onToggleSearch={() => setSearchOpen((o) => !o)} />
        </AppShell.Header>

        <AppShell.Navbar className={classes.navbar} component="nav" aria-label="Sections">
          <NavItems />
          <NavFooter />
        </AppShell.Navbar>

        <AppShell.Main className={classes.main}>
          <ViewAsBanner />
          <AnnouncementsBanner />
          <div ref={mainRef} className={classes.content} data-mobile={isMobile || undefined}>
            {transitioning && <PageSkeleton />}
            <div hidden={transitioning} style={{ display: transitioning ? 'none' : 'contents' }}>
              <Suspense fallback={<PageSkeleton />}>
                <Outlet />
              </Suspense>
            </div>
          </div>
        </AppShell.Main>

        {isMobile && (
          <>
            <AppShell.Footer>
              <BottomBar
                menuOpen={menuOpen}
                onToggleMenu={() => setMenuOpen((o) => !o)}
                onNavigate={() => setMenuOpen(false)}
              />
            </AppShell.Footer>
            <MobileDrawer opened={menuOpen} onClose={() => setMenuOpen(false)} />
          </>
        )}
      </AppShell>
    </RealtimeProvider>
  );
}
